import type { CoachingAccessKey } from '@mlm/shared';
import type {
  BreathworkPattern,
  FrequencyCategory,
  FrequencyTrack,
  ManifestationPractice,
  Mantra,
  Meditation,
} from './api';
import {
  useBreathwork,
  useFeature,
  useFrequencies,
  useManifestationPractices,
  useMantras,
  useMeditations,
} from './queries';

/**
 * One collection as Home shows it: `off` hides the rail (the tenant switched
 * the tool off), `locked` shows a single plan tile in its place (a higher plan
 * opens it), `on` lists what the member can open.
 */
export type HomeCollection<T> = {
  feature: CoachingAccessKey;
  state: 'on' | 'locked' | 'off';
  items: T[];
};

function collection<T>(
  feature: CoachingAccessKey,
  state: 'on' | 'locked' | 'off',
  data: { locked?: boolean } | undefined,
  items: T[] | undefined,
): HomeCollection<T> {
  if (state === 'off') return { feature, state, items: [] };
  if (state === 'locked' || data?.locked) return { feature, state: 'locked', items: [] };
  return { feature, state: 'on', items: items ?? [] };
}

/**
 * Every tool library Home draws a rail from, fetched only where the member's
 * plan opens the tool, so a Free member's Home sends no request it knows will
 * be refused.
 */
export function useHomeLibraries() {
  const breathState = useFeature('breathwork');
  const frequencyState = useFeature('frequencies');
  const mantraState = useFeature('mantras');
  const meditationState = useFeature('meditation');
  const toolkitState = useFeature('manifestation_toolkit');

  const breath = useBreathwork(breathState === 'on');
  const frequencies = useFrequencies(frequencyState === 'on');
  const mantras = useMantras(mantraState === 'on');
  const meditations = useMeditations(meditationState === 'on');
  const toolkit = useManifestationPractices(toolkitState === 'on');

  return {
    breath: collection<BreathworkPattern>(
      'breathwork',
      breathState,
      breath.data,
      breath.data?.patterns,
    ),
    frequencies: collection<FrequencyTrack>(
      'frequencies',
      frequencyState,
      frequencies.data,
      frequencies.data?.tracks,
    ),
    frequencyCategories: frequencies.data?.categories ?? ([] as FrequencyCategory[]),
    mantras: collection<Mantra>('mantras', mantraState, mantras.data, mantras.data?.mantras),
    meditations: collection<Meditation>(
      'meditation',
      meditationState,
      meditations.data,
      meditations.data?.meditations,
    ),
    toolkit: collection<ManifestationPractice>(
      'manifestation_toolkit',
      toolkitState,
      undefined,
      toolkit.data?.practices,
    ),
  };
}

export type HomeLibraries = ReturnType<typeof useHomeLibraries>;
